import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listRayons,
  createRayon,
  updateRayon,
  setUserRayon,
  setEventRayon,
} from '@/lib/api/rayons';
import { useAuth } from '@/hooks/useAuth';

const KEYS = { all: ['rayons'] as const };

export const useRayons = () => useQuery({ queryKey: KEYS.all, queryFn: listRayons });

function useRayonMutation<TArgs>(fn: (a: TArgs) => Promise<void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export const useCreateRayon = () =>
  useRayonMutation((a: { code: string; name: string }) => createRayon(a.code, a.name));

export const useUpdateRayon = () =>
  useRayonMutation(
    (a: { id: string; patch: { code?: string; name?: string; isActive?: boolean } }) =>
      updateRayon(a.id, a.patch),
  );

/**
 * Changing your own rayon changes what you can see, and the rayon lives in the
 * token — so the session has to be refreshed or the app keeps showing a scope
 * the database has stopped granting.
 */
export const useSetUserRayon = () => {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  return useMutation({
    mutationFn: (a: { uid: string; rayonId: string | null }) => setUserRayon(a.uid, a.rayonId),
    onSuccess: async (_d, a) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      if (a.uid === user?.uid) await refresh();
    },
  });
};

export const useSetEventRayon = () =>
  useRayonMutation((a: { eventId: string; rayonId: string | null }) =>
    setEventRayon(a.eventId, a.rayonId),
  );
